import { ILoader } from '../../../../Assembler/dist/base/loader/iLoader.js';
import type { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { DynamicResultsJson } from '../../model/dynamicResultsJson.js';

/**
 * GetTemplateJsonResult - Return type for getTemplate in NormalJson loaders
 */
export interface GetTemplateJsonResult {
    htmlJson: JsonObject;
    dataJson: JsonObject | null;
    resolvedTemplateName: string;
    resolvedAppSite: string;
}

/**
 * ILoaderNormalJson - Loader interface specifically for EngineNormalJson
 *
 * This is a marker interface (like C# interfaces) - no method bodies.
 * Extends ILoader with NormalJson-specific getTemplate() return type.
 * Actual implementation is in LoaderNormalJson and loaderNormalJson_*.ts partial files.
 *
 * @method setDynamicResults(dynamicResults) - Sets the DynamicResultsJson instance for the current merge
 * @method getTemplate(appSite, currentTemplateName, appFileOrPlaceholderOrSlotholderName, appView?, flowStack?, configJson?)
 *   Gets a template's HtmlJson and DataJson with AppView and SearchAppSites fallback support.
 *   Returns: {htmlJson: Object, dataJson: Object|null, resolvedTemplateName: string, resolvedAppSite: string}
 *
 * @method applyDynamicViewHandlersJson(appSite, appView, currentTemplateName, componentName, componentDepth, componentHtmlJson, componentDataJson, resolvedAppSite, resolvedComponentName, configJson?)
 *   Applies DynamicView handlers to wrap/modify component HtmlJson. Called by Engine after getTemplate.
 *   Returns: Object (modified HtmlJson)
 *
 * IMPORTANT CONTRACT:
 * - currentTemplateName MUST be a clean template name WITHOUT AppSite prefix
 * - Example: "MainContent" NOT "JsonRule5A_MainContent"
 */
export abstract class ILoaderNormalJson extends ILoader {
    abstract setDynamicResults(_dynamicResults: DynamicResultsJson): void;
    abstract getTemplatesApiResponse(
        _appSite: string,
        _appView: string,
        _contextJson: JsonObject,
        _serverTimeMs: number,
        _includeSearchAppSites?: boolean,
        _maskHtml?: boolean,
        _orderedTemplateNames?: string[] | null
    ): string;
    abstract getTemplate(_appSite: string, _currentTemplateName: string, _appFileOrPlaceholderOrSlotholderName: string, _appView?: string | null, _flowStack?: string[] | null, _configJson?: JsonObject | null): GetTemplateJsonResult;
    abstract getTemplateWithView(_appSite: string, _currentTemplateName: string, _componentName: string, _appView?: string | null, _flowStack?: string[] | null, _configJson?: JsonObject | null, _clientMode?: boolean): GetTemplateJsonResult;
    abstract applyDynamicViewHandlersJson(_appSite: string, _appView: string, _currentTemplateName: string, _componentName: string, _componentDepth: number, _componentHtmlJson: JsonObject, _componentDataJson: JsonObject | null, _resolvedAppSite: string, _resolvedComponentName: string, _configJson?: JsonObject | null): JsonObject;
}
